// A conservative backing-store budget also covers browsers without deviceMemory.
// CSS/world coordinates stay unchanged; only raster density is reduced when needed.
export function getCanvasRasterSize(width, height, deviceMemory = 4) {
  const w = Math.max(1, Number(width) || 1);
  const h = Math.max(1, Number(height) || 1);
  const maxPixels = deviceMemory <= 2 ? 3 * 1024 * 1024 : 6 * 1024 * 1024;
  const scale = Math.min(1, 4096 / w, 4096 / h, Math.sqrt(maxPixels / (w * h)));
  return {
    scale,
    width: Math.max(1, Math.floor(w * scale)),
    height: Math.max(1, Math.floor(h * scale)),
  };
}

export function releaseCanvas(canvas) {
  if (!canvas) return;
  // Explicitly release the pixel buffer rather than waiting for garbage collection.
  canvas.width = 0;
  canvas.height = 0;
}

export function clearCanvasCache(cache) {
  cache.forEach(releaseCanvas);
  cache.clear();
}

const lostRenderCanvases = new WeakSet();

export function createRenderCanvas() {
  const canvas = document.createElement("canvas");
  // A restored offscreen buffer is empty. Never reuse it as a saved page/snapshot.
  canvas.addEventListener("contextlost", () => lostRenderCanvases.add(canvas));
  return canvas;
}

export function isCanvasUsable(canvas) {
  if (!canvas || lostRenderCanvases.has(canvas)) return false;
  const ctx = canvas?.getContext("2d");
  return !!ctx && !ctx.isContextLost?.();
}

export function installCanvasRecovery({ canvas, overlay, invalidate, redraw, hasRendered }) {
  const recover = () => {
    // Discard ALL offscreen copies, otherwise a redraw can reproduce a lost bitmap.
    invalidate();
    redraw();
  };
  const onLost = () => {
    invalidate();
    // Do not preventDefault: that cancels the browser's automatic 2D restoration.
  };
  const check = () => {
    if (document.visibilityState === "hidden" || !hasRendered()) return;
    try {
      const ctx = canvas.getContext("2d");
      if (!ctx || ctx.isContextLost?.()) return;
      const x = Math.floor(canvas.width / 2);
      const y = Math.floor(canvas.height / 2);
      if (ctx.getImageData(x, y, 1, 1).data[3] === 0) recover();
    } catch {
      // Cross-origin images may taint a canvas; restoration events still work.
    }
  };
  for (const target of [canvas, overlay]) {
    target?.addEventListener("contextlost", onLost);
    target?.addEventListener("contextrestored", recover);
  }
  const interval = window.setInterval(check, 1000);
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", check);
  return () => {
    window.clearInterval(interval);
    window.removeEventListener("focus", check);
    document.removeEventListener("visibilitychange", check);
    for (const target of [canvas, overlay]) {
      target?.removeEventListener("contextlost", onLost);
      target?.removeEventListener("contextrestored", recover);
    }
  };
}
